/**
 * Configure Order page — /buy-leads/configure?o={orderId}
 *
 * Renders off the live order status:
 *   paid       -> 2-step wizard (email -> filters/Generate)
 *   generating -> live progress (files[] populate as the orchestrator runs)
 *   fulfilled  -> delivery summary
 *   error      -> calm "we're on it" message (never raw errors)
 *   anything else / missing -> redirect home
 */

import { useEffect, useRef, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { PortalHeader } from '../Layout/PortalHeader';
import { useLeadOrder, LeadOrderFile } from './useLeadOrder';
import { StepEmail } from './StepEmail';
import { StepFilters } from './StepFilters';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <PortalHeader showCredits={false} />
      <div className="container" style={{ maxWidth: '820px' }}>
        <Link to="/" className="back-link" style={{ display: 'inline-block', marginBottom: '16px' }}>
          ← Back to Dashboard
        </Link>
        {children}
      </div>
    </div>
  );
}

function GeneratingView({ files }: { files: LeadOrderFile[] }) {
  return (
    <div className="buyleads-card">
      <h2 style={{ margin: '0 0 8px', color: 'var(--gold)' }}>Preparing Your Leads</h2>
      <p style={{ color: 'var(--gray-400)', fontSize: '0.9rem', marginBottom: '16px' }}>
        We're generating your files now. You'll receive them by email, and they'll be loaded into your
        AI caller queue for Run 1. This page updates automatically.
      </p>
      {files.length === 0 ? (
        <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>Starting up…</p>
      ) : (
        files.map((f, idx) => (
          <div key={idx} className="buyleads-file-row">
            <div>
              <div style={{ color: 'var(--white)', fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {f.fileName || `${f.pricePoint} leads`}
              </div>
              {typeof f.requested === 'number' && (
                <div style={{ color: 'var(--gray-500)', fontSize: '0.78rem', marginTop: '2px' }}>
                  {f.pricePoint} · requested {f.requested.toLocaleString('en-US')}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', fontSize: '0.82rem' }}>
              {typeof f.delivered === 'number' ? (
                <span style={{ color: 'var(--green)', fontWeight: 600 }}>
                  {f.delivered.toLocaleString('en-US')} delivered
                </span>
              ) : (
                <span style={{ color: 'var(--gray-500)' }}>working…</span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function FulfilledView({ files }: { files: LeadOrderFile[] }) {
  return (
    <div className="buyleads-card">
      <h2 style={{ margin: '0 0 8px', color: 'var(--green)' }}>Your Leads Are Ready</h2>
      <p style={{ color: 'var(--gray-300)', fontSize: '0.9rem', marginBottom: '16px' }}>
        Check your email — your files are attached. Your leads are also loaded into your AI caller
        queue in <strong>Run 1</strong>, ready to send.
      </p>
      {files.map((f, idx) => (
        <div key={idx} className="buyleads-file-row">
          <div style={{ color: 'var(--white)', fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {f.fileName || `${f.pricePoint} leads`}
          </div>
          <div style={{ color: 'var(--green)', fontSize: '0.82rem', fontWeight: 600 }}>
            {typeof f.delivered === 'number' ? `${f.delivered.toLocaleString('en-US')} leads` : 'delivered'}
          </div>
        </div>
      ))}
      <div style={{ marginTop: '18px' }}>
        <Link to="/" className="btn btn-primary">Go to Dashboard</Link>
      </div>
    </div>
  );
}

function ErrorView() {
  return (
    <div className="buyleads-card">
      <h2 style={{ margin: '0 0 8px', color: 'var(--gold)' }}>We're Preparing Your Leads</h2>
      <p style={{ color: 'var(--gray-300)', fontSize: '0.95rem' }}>
        We're preparing your leads — you'll receive them by email shortly.
      </p>
      <div style={{ marginTop: '18px' }}>
        <Link to="/" className="btn btn-secondary">Back to Dashboard</Link>
      </div>
    </div>
  );
}

export function ConfigureOrder() {
  const { orderId, order, loading, exists } = useLeadOrder();
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [deliveryEmail, setDeliveryEmail] = useState('');

  // Fire-and-forget prewarms the moment a paid order's wizard opens: the
  // engine's pool cache (availability speed) and the submit function's
  // instance (so Generate doesn't sit in a cold start).
  const warmedRef = useRef(false);
  useEffect(() => {
    if (order?.status === 'paid' && !warmedRef.current) {
      warmedRef.current = true;
      httpsCallable(functions, 'warmLeadOrderEngine', { timeout: 120000 })({}).catch(() => {
        /* best-effort */
      });
      httpsCallable(functions, 'submitLeadOrderConfig', { timeout: 30000 })({ warm: true }).catch(() => {
        /* best-effort */
      });
    }
  }, [order?.status]);

  if (loading) {
    return <Shell><div className="loading">Loading your order…</div></Shell>;
  }

  // No order id, doc missing, or read denied -> home.
  if (!orderId || !exists || !order) {
    return <Navigate to="/" replace />;
  }

  const { status } = order;

  // Only paid / generating / fulfilled / error are actionable here.
  if (status !== 'paid' && status !== 'generating' && status !== 'fulfilled' && status !== 'error') {
    return <Navigate to="/" replace />;
  }

  return (
    <Shell>
      {status === 'paid' && wizardStep === 1 && (
        <StepEmail
          deliveryEmail={deliveryEmail}
          onEmailChange={setDeliveryEmail}
          onNext={() => setWizardStep(2)}
        />
      )}
      {status === 'paid' && wizardStep === 2 && (
        <StepFilters
          orderId={orderId}
          items={order.items}
          deliveryEmail={deliveryEmail}
          onBack={() => setWizardStep(1)}
        />
      )}
      {status === 'generating' && <GeneratingView files={order.files} />}
      {status === 'fulfilled' && <FulfilledView files={order.files} />}
      {status === 'error' && <ErrorView />}
    </Shell>
  );
}
